import { useState } from "react";
import NetWorthChart from "@/features/dashboard/NetWorthChart";
import WhatYouOwn from "@/features/dashboard/WhatYouOwn";

const TIMEFRAMES = ["1M", "3M", "6M", "1Y", "All time"];

// A year of illustrative fortnightly snapshots, gently rising with dips.
const POINTS = (() => {
  const values = [
    8224200, 8251000, 8198400, 8262300, 8305100, 8288700, 8341900, 8396200,
    8360800, 8419500, 8471300, 8450200, 8503900, 8548100, 8521700, 8577400,
    8612800, 8589300, 8606100, 8578900, 8634500, 8601200, 8618700, 8642500,
  ];
  const start = Date.UTC(2025, 9, 28);
  return values.map((value, i) => ({
    date: new Date(start + i * 15 * 86400000).toISOString().slice(0, 10),
    value,
  }));
})();

// Illustrative holdings for the "What you own" rows; they sum to the
// assets figure in the footer.
const ASSETS = [
  { asset_type: "mutual_fund", current_value: 7010000 },
  { asset_type: "bank_accounts", current_value: 1560000 },
  { asset_type: "personal", current_value: 540000 },
  { asset_type: "travel_points", total_value_inr: 300000 },
];

/**
 * Product shot of the dashboard overview with illustrative numbers. It
 * renders the real chart and "What you own" components, so it stays in
 * sync with the dashboard by construction.
 */
export default function LandingPreview() {
  const [hover, setHover] = useState(null);

  return (
    <section className="mx-auto max-w-[800px] px-6 pb-16 sm:pb-24">
      <div className="rounded-[20px] border border-vaulzo-hairline bg-white px-6 pb-8 pt-10 shadow-[0_18px_50px_rgba(23,24,26,0.07)] sm:px-12">
        {/* Net worth hero, as on the dashboard */}
        <div className="mb-8 text-center">
          <div className="mb-4 text-[12px] uppercase tracking-[0.14em] text-vaulzo-fainter">
            Net worth
          </div>
          <div className="num text-[44px] font-bold leading-[0.92] tracking-[-0.045em] text-vaulzo-ink sm:text-[60px]">
            ₹86,42,500
          </div>
          <div className="num mt-[10px] text-sm font-medium text-vaulzo-fainter">
            ≈ $90,780
          </div>
          <div className="num mt-4 text-[15px] font-semibold text-vaulzo-accent">
            ↑ ₹4,18,300 · 5.1% in the last year
          </div>
          <div className="mt-[6px] text-[12px] text-vaulzo-subtle">as of 28 Oct 2026</div>
        </div>

        {/* Timeframe segmented control (decorative) */}
        <div className="mb-7 flex justify-center">
          <div className="inline-flex gap-[2px] rounded-[11px] bg-vaulzo-row p-1">
            {TIMEFRAMES.map((label, i) => (
              <span
                key={label}
                className={`rounded-lg px-[13px] py-[6px] text-[12.5px] sm:px-[15px] sm:text-[13px] ${
                  i === 3
                    ? "bg-white font-semibold text-vaulzo-ink shadow-[0_1px_2px_rgba(0,0,0,0.06)]"
                    : "font-medium text-vaulzo-faint"
                }`}
              >
                {label}
              </span>
            ))}
          </div>
        </div>

        <NetWorthChart points={POINTS} hoverIndex={hover} onHover={setHover} />

        <div className="mt-12">
          <WhatYouOwn assets={ASSETS} />
        </div>

        {/* Assets / Debts split footer, as on the dashboard */}
        <div className="mt-10 h-px bg-vaulzo-hairline" />
        <div className="flex">
          <div className="flex-1 border-r border-vaulzo-hairline pr-6 pt-5 text-left">
            <div className="mb-2 text-[13px] text-vaulzo-fainter">
              Assets <span className="text-vaulzo-accent">→</span>
            </div>
            <div className="num text-[22px] font-bold tracking-[-0.02em] text-vaulzo-ink">
              ₹94,10,000
            </div>
          </div>
          <div className="flex-1 pl-6 pt-5 text-right">
            <div className="mb-2 text-[13px] text-vaulzo-fainter">
              Debts <span className="text-vaulzo-accent">→</span>
            </div>
            <div className="num text-[22px] font-bold tracking-[-0.02em] text-vaulzo-danger">
              ₹7,67,500
            </div>
          </div>
        </div>
      </div>

      <p className="mt-4 text-center text-[12px] text-vaulzo-subtle">
        Illustration — your dashboard charts your own snapshots.
      </p>
    </section>
  );
}
